import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRegionManagement } from '../hooks/useRegionManagement';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetRegionsQuery,
  useGetTSPsQuery,
  useGetZonesQuery,
} from '../services/edgeApi';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { BadgeDisplay } from '../helpers/BadgeDisplay';
import { HorizontalDivider } from '../helpers/HorizontalDivider';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  FilterIcon,
  PlusIcon,
} from '@iconicicons/react';
import { Region } from '@/types';
import { mapActions } from '../map/slice/mapSlice';
import { useTspFilter } from '../helpers/hooks';
import { MapEventListener, useMapContext } from '../map/context/MapContext';
import { selectAllEdges, selectTsps } from '../map/slice/mapSelectors';
import { TSPBadge } from '../helpers/TSPBadge';
import { ZoneBadge } from '../helpers/ZoneBadge';
export type RegionTableProps = {};

const MAXIMUM_LIST_ITEMS = 3;

export const RegionTable: React.FC<RegionTableProps> = () => {
  useGetTSPsQuery();
  const { data: zones } = useGetZonesQuery(undefined);

  const { data: regions, isLoading, error } = useGetRegionsQuery(undefined);
  const dispatch = useDispatch();
  const setTSPFilter = useTspFilter();
  const edges = useSelector(selectAllEdges);
  const tsps = useSelector(selectTsps);
  const {
    hoverListeners,
    registerHoverListener,
    unregisterHoverListener,
    clickListeners,
    registerClickListener,
    unregisterClickListener,
  } = useMapContext();

  const {
    addPipeToRegion,
    removePipeFromRegion,
    addZoneToRegion,
    removeZoneFromRegion,
  } = useRegionManagement();

  const [expandedRegions, setExpandedRegions] = useState<Set<number>>(
    new Set<number>(),
  );
  const [regionIdToAddTSP, _setRegionIdToAddTSP] = useState<number | null>(
    null,
  );
  const [regionIdToAddZone, _setRegionIdToAddZone] = useState<number | null>(
    null,
  );

  const regionToAddTSP = useMemo(() => {
    return regions?.find((region) => region.id === regionIdToAddTSP) ?? null;
  }, [regionIdToAddTSP, regions]);

  const regionToAddZone = useMemo(() => {
    return regions?.find((region) => region.id === regionIdToAddZone) ?? null;
  }, [regionIdToAddZone, regions]);

  const defaultHighlightPayload: { tsps: Set<number>; zones: Set<number> } =
    useMemo(() => {
      if (regionToAddZone) {
        return {
          tsps: new Set<number>(
            regionToAddZone.tsps.map((tsp) => tsp.id) ?? [],
          ),
          zones: new Set<number>(
            regionToAddZone.zones.map((zone) => zone.id) ?? [],
          ),
        };
      }
      if (regionToAddTSP) {
        return {
          tsps: new Set<number>(regionToAddTSP.tsps.map((tsp) => tsp.id) ?? []),
          zones: new Set<number>(
            regionToAddTSP.zones.map((zone) => zone.id) ?? [],
          ),
        };
      }
      return {
        tsps: new Set<number>(),
        zones: new Set<number>(),
      };
    }, [regionToAddZone, regionToAddTSP]);

  useEffect(() => {
    dispatch(mapActions.setHighlight(defaultHighlightPayload));
  }, [defaultHighlightPayload]);

  const setRegionToAddTSP = useCallback<typeof _setRegionIdToAddTSP>(
    (regionId) => {
      if (regionIdToAddZone) {
        _setRegionIdToAddZone(null);
      }
      _setRegionIdToAddTSP(regionId);
    },
    [regionIdToAddZone],
  );

  const setRegionToAddZone = useCallback<typeof _setRegionIdToAddZone>(
    (regionId) => {
      if (regionIdToAddTSP) {
        _setRegionIdToAddTSP(null);
      }
      _setRegionIdToAddZone(regionId);
    },
    [regionIdToAddTSP],
  );

  const onHoverPipe: MapEventListener = useCallback(
    (feature) => {
      if (!feature?.getProperties()?.key?.startsWith('edge-')) {
        highlightPipe(undefined);
        return;
      }
      const edgeId = feature?.getProperties()?.id;
      const edge = edges?.[edgeId];
      highlightPipe(edge?.tsp_id);
    },
    [edges, defaultHighlightPayload],
  );

  const onSelectPipe: MapEventListener = useCallback(
    async (feature) => {
      if (!feature?.getProperties()?.key?.startsWith('edge-')) {
        highlightPipe(undefined);
        return;
      }
      const edgeId = feature?.getProperties()?.id;
      const edge = edges?.[edgeId];
      const tsp = tsps[edge?.tsp_id];
      if (!edge || !regionToAddTSP || !tsp) return;
      await addPipeToRegion(regionToAddTSP, tsp);
      highlightPipe(undefined);
    },
    [edges, regionToAddTSP, defaultHighlightPayload],
  );

  const onHoverZone: MapEventListener = useCallback(
    (feature) => {
      if (!feature?.getProperties()?.key?.startsWith('edge-')) {
        highlightZone(undefined);
        return;
      }
      const edgeId = feature?.getProperties()?.id;
      const edge = edges?.[edgeId];
      highlightZone(edge?.zone_id);
    },
    [edges, defaultHighlightPayload],
  );

  const onSelectZone: MapEventListener = useCallback(
    async (feature) => {
      if (!feature?.getProperties()?.key?.startsWith('edge-')) {
        highlightZone(undefined);
        return;
      }
      const edgeId = feature?.getProperties()?.id;
      const edge = edges?.[edgeId];
      const zone = zones?.[edge?.zone_id];
      if (!edge || !regionToAddZone || !zone) return;
      addZoneToRegion(regionToAddZone, zone);
      highlightZone(undefined);
    },
    [edges, regionToAddZone, defaultHighlightPayload],
  );

  const highlightRegion = (region?: Region) => {
    if (!region) {
      dispatch(mapActions.setHighlight(defaultHighlightPayload));
      return;
    }
    dispatch(
      mapActions.setHighlight({
        tsps: new Set(region.tsps.map((tsp) => tsp.id)),
        zones: new Set(region.zones.map((zone) => zone.id)),
      }),
    );
  };

  const highlightPipe = (tsp?: number) => {
    if (!tsp) {
      dispatch(mapActions.setHighlight(defaultHighlightPayload));
      return;
    }
    dispatch(
      mapActions.setHighlight({
        tsps: new Set([...defaultHighlightPayload.tsps, tsp]),
        zones: defaultHighlightPayload.zones,
      }),
    );
  };

  const highlightZone = (zone?: number) => {
    if (!zone) {
      dispatch(mapActions.setHighlight(defaultHighlightPayload));
      return;
    }
    dispatch(
      mapActions.setHighlight({
        tsps: defaultHighlightPayload.tsps,
        zones: new Set([...defaultHighlightPayload.zones, zone]),
      }),
    );
  };

  const filterRegion = (region: Region) => {
    const tspSetByZone = new Set(
      region.zones.filter((zone) => zone.tsp).map((zone) => zone.tsp!.id),
    );
    const tspSetByPipe = new Set(region.tsps.map((tsp) => tsp.id));
    const tspSet = new Set([...tspSetByPipe, ...tspSetByZone]);
    setTSPFilter([...tspSet]);
  };

  useEffect(() => {
    if (regionToAddTSP) {
      const currentHoverListeners = Array.from(hoverListeners.current ?? []);
      for (const listener of currentHoverListeners) {
        unregisterHoverListener(listener);
      }
      const currentClickListeners = Array.from(clickListeners.current ?? []);
      for (const listener of currentClickListeners) {
        unregisterClickListener(listener);
      }
      registerHoverListener(onHoverPipe);
      registerClickListener(onSelectPipe);
      return () => {
        unregisterHoverListener(onHoverPipe);
        unregisterClickListener(onSelectPipe);
        for (const listener of currentHoverListeners) {
          registerHoverListener(listener);
        }
        for (const listener of currentClickListeners) {
          registerClickListener(listener);
        }
      };
    }
    return () => null;
  }, [
    registerHoverListener,
    unregisterHoverListener,
    onHoverPipe,
    regionToAddTSP,
  ]);

  useEffect(() => {
    if (regionToAddZone) {
      const currentHoverListeners = Array.from(hoverListeners.current ?? []);
      for (const listener of currentHoverListeners) {
        unregisterHoverListener(listener);
      }
      const currentClickListeners = Array.from(clickListeners.current ?? []);
      for (const listener of currentClickListeners) {
        unregisterClickListener(listener);
      }
      registerHoverListener(onHoverZone);
      registerClickListener(onSelectZone);
      return () => {
        unregisterHoverListener(onHoverZone);
        unregisterClickListener(onSelectZone);
        for (const listener of currentHoverListeners) {
          registerHoverListener(listener);
        }
        for (const listener of currentClickListeners) {
          registerClickListener(listener);
        }
      };
    }
    return () => null;
  }, [
    registerHoverListener,
    unregisterHoverListener,
    onHoverZone,
    regionToAddZone,
  ]);

  return (
    <table className="table" style={{ position: 'relative' }}>
      <thead
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 2,
        }}
      >
        <tr>
          <th>Region</th>
          <th className="text-center">Pipelines</th>
          <th className="text-center">Zones</th>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={3}>
              <div className="d-flex justify-content-center align-items-center my-3 w-100">
                <LoadingIndicator />
              </div>
            </td>
          </tr>
        ) : error ? (
          <tr>
            <td colSpan={3}>
              <div className="d-flex justify-content-center align-items-center my-3 w-100 text-danger">
                Error loading regions
              </div>
            </td>
          </tr>
        ) : (
          regions
            ?.slice()
            .sort((a, b) => a.id - b.id)
            .map((region) => (
              <Fragment key={region.id.toString()}>
                <tr key={region.id} style={{ borderBottom: 0 }}>
                  <td style={{ borderBottom: 0 }}>
                    <span
                      className="d-flex align-items-center"
                      style={{
                        position: 'sticky',
                        top: 48,
                        zIndex: 1,
                      }}
                    >
                      <FilterIcon
                        onClick={() => filterRegion(region)}
                        role="button"
                        className="me-2"
                      />
                      <span
                        onMouseEnter={() => highlightRegion(region)}
                        onMouseLeave={() => highlightRegion(undefined)}
                      >
                        <span className="fw-bold">#{region.id}</span>{' '}
                        {region.name}
                      </span>
                    </span>
                  </td>
                  <td style={{ borderBottom: 0 }}>
                    <div className="d-flex flex-column align-items-center">
                      <BadgeDisplay
                        role="button"
                        style={{ width: '100%' }}
                        onClick={() =>
                          setRegionToAddTSP((p) =>
                            p === region.id ? null : region.id,
                          )
                        }
                      >
                        {regionToAddTSP?.id === region.id ? (
                          'Cancel'
                        ) : (
                          <PlusIcon />
                        )}
                      </BadgeDisplay>
                      {(expandedRegions.has(region.id)
                        ? region.tsps
                        : region.tsps.slice(0, MAXIMUM_LIST_ITEMS)
                      ).map((tsp) => (
                        <TSPBadge
                          key={tsp.id}
                          tsp={tsp}
                          style={{ width: '100%' }}
                          onMouseEnter={() => highlightPipe(tsp.id)}
                          onMouseLeave={() => highlightPipe(undefined)}
                          extraContent={
                            <CloseIcon
                              role="button"
                              onClick={(evt) => {
                                evt.stopPropagation();
                                removePipeFromRegion(region, tsp);
                              }}
                            />
                          }
                        />
                      ))}
                      {region.tsps.length > MAXIMUM_LIST_ITEMS &&
                        !expandedRegions.has(region.id) && (
                          <span className="text-muted text-center">
                            {region.tsps.length - MAXIMUM_LIST_ITEMS} more
                          </span>
                        )}
                    </div>
                  </td>
                  <td style={{ borderBottom: 0 }}>
                    <div className="d-flex flex-column align-items-center">
                      <BadgeDisplay
                        role="button"
                        style={{ width: '100%' }}
                        onClick={() =>
                          setRegionToAddZone((p) =>
                            p === region.id ? null : region.id,
                          )
                        }
                      >
                        {regionToAddZone?.id === region.id ? (
                          'Cancel'
                        ) : (
                          <PlusIcon />
                        )}
                      </BadgeDisplay>
                      {(expandedRegions.has(region.id)
                        ? region.zones
                        : region.zones.slice(0, MAXIMUM_LIST_ITEMS)
                      ).map((zone) => (
                        <ZoneBadge
                          role="button"
                          zone={zone}
                          key={zone.id}
                          style={{ width: '100%' }}
                          onMouseEnter={() => highlightZone(zone.id)}
                          onMouseLeave={() => highlightZone(undefined)}
                          extraContent={
                            <CloseIcon
                              role="button"
                              onClick={(evt) => {
                                evt.stopPropagation();
                                removeZoneFromRegion(region, zone);
                              }}
                            />
                          }
                        />
                      ))}
                      {region.zones.length > MAXIMUM_LIST_ITEMS &&
                        !expandedRegions.has(region.id) && (
                          <span className="text-muted text-center">
                            {region.zones.length - MAXIMUM_LIST_ITEMS} more
                          </span>
                        )}
                    </div>
                  </td>
                </tr>

                <tr key={`divider-${region.id}`} style={{ borderTop: 0 }}>
                  <td colSpan={3} style={{ borderTop: 0 }}>
                    {(region.zones.length > MAXIMUM_LIST_ITEMS ||
                      region.tsps.length > MAXIMUM_LIST_ITEMS) && (
                      <HorizontalDivider
                        onClick={() => {
                          setExpandedRegions((prev) => {
                            const newSet = new Set(prev);
                            if (newSet.has(region.id)) {
                              newSet.delete(region.id);
                            } else {
                              newSet.add(region.id);
                            }
                            return newSet;
                          });
                        }}
                      >
                        {expandedRegions.has(region.id) ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )}
                      </HorizontalDivider>
                    )}
                  </td>
                </tr>
              </Fragment>
            ))
        )}
      </tbody>
    </table>
  );
};
